import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Download, RefreshCw, Filter, TrendingUp, Users, Compass, Sparkles, DollarSign, Globe } from 'lucide-react';
import { api } from '@/lib/api';
import { getAuthToken } from '@/lib/api';

const REPORT_TABS = [
  { k: 'behavioral', l: 'Behavioral Insights', icon: Users },
  { k: 'content',    l: 'Content Performance', icon: BarChart3 },
  { k: 'discovery',  l: 'Discovery Analytics', icon: Compass },
  { k: 'suggestions', l: 'Suggestion Engine', icon: Sparkles },
  { k: 'commerce',   l: 'Commerce', icon: DollarSign },
  { k: 'traffic',    l: 'Traffic', icon: Globe },
];

function Card({ title, value, sub }) {
  return (
    <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
      <p className="pg-label text-[10px]">{title}</p>
      <p className="font-serif text-2xl text-[var(--pg-ink)] mt-1">{value}</p>
      {sub && <p className="text-[10px] text-[var(--pg-ink-faint)] font-mono mt-1">{sub}</p>}
    </div>
  );
}

function BarRow({ label, value, max, color = 'var(--pg-gold-2)' }) {
  const pct = Math.min(100, Math.round((value / (max || 1)) * 100));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px] font-mono text-[var(--pg-ink-muted)]">
        <span className="truncate max-w-[70%]">{label}</span>
        <span className="text-[var(--pg-ink)]">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
        <div style={{ width: `${pct}%`, background: color }} className="h-full" />
      </div>
    </div>
  );
}

function ExportButtons({ report, filters }) {
  const trigger = (fmt) => {
    const params = new URLSearchParams({ report, fmt, ...(filters || {}) });
    const token = getAuthToken();
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    // We need an authenticated download. Simplest reliable path: window.open with fetch → blob.
    fetch(`${base}/api/admin/intelligence/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error('Export failed'); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `planet-g-${report}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      })
      .catch((e) => alert(e.message || 'Export failed'));
  };
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => trigger('csv')} className="pg-btn-ghost px-2 py-1 rounded-[var(--pg-radius-sm)] text-[11px] flex items-center gap-1" data-testid={`export-${report}-csv`}>
        <Download size={12} /> CSV
      </button>
      <button onClick={() => trigger('xlsx')} className="pg-btn-ghost px-2 py-1 rounded-[var(--pg-radius-sm)] text-[11px] flex items-center gap-1" data-testid={`export-${report}-xlsx`}>
        <Download size={12} /> Excel
      </button>
    </div>
  );
}

export default function BehavioralInsights() {
  const [report, setReport] = useState('behavioral');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ start: '', end: '', content_type: '', device: '' });

  const filterParams = useMemo(() => Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), [filters]);

  const load = async () => {
    setBusy(true);
    try {
      let endpoint = 'behavioral';
      if (report === 'content')    endpoint = 'content-performance';
      if (report === 'discovery')  endpoint = 'discovery-analytics';
      if (report === 'suggestions') endpoint = 'suggestion-analytics';
      if (report === 'commerce')   endpoint = 'commerce-analytics';
      if (report === 'traffic')    endpoint = 'traffic-analytics';
      const { data: d } = await api.get(`/admin/intelligence/${endpoint}`, { params: filterParams });
      setData(d);
    } catch (e) { setData({ error: e?.response?.data?.detail || 'Load failed' }); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [report]);

  const applyFilters = () => load();

  return (
    <div className="space-y-4" data-testid="behavioral-insights">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Behavioral Insights & Analytics</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><RefreshCw size={14} /> Refresh</button>
          <ExportButtons report={report} filters={filterParams} />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {REPORT_TABS.map(({ k, l, icon: Icon }) => (
          <button key={k} onClick={() => setReport(k)}
                  data-testid={`intel-tab-${k}`}
                  className="px-3 py-1.5 rounded-full border text-[10px] font-mono tracking-[0.24em] uppercase flex items-center gap-1.5"
                  style={{
                    background: report === k ? 'rgba(226,199,122,0.14)' : 'transparent',
                    borderColor: report === k ? 'rgba(226,199,122,0.6)' : 'var(--pg-border-2)',
                    color: report === k ? 'var(--pg-gold-2)' : 'var(--pg-ink-muted)',
                  }}>
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>

      <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4 grid grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Start</label>
          <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-2 py-1.5 text-xs" />
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">End</label>
          <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-2 py-1.5 text-xs" />
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Content type</label>
          <select value={filters.content_type} onChange={(e) => setFilters({ ...filters, content_type: e.target.value })} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-2 py-1.5 text-xs">
            <option value="">Any</option>
            {['article','audio_item','video','product','gallery_item','mobile_read'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Device</label>
            <select value={filters.device} onChange={(e) => setFilters({ ...filters, device: e.target.value })} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-2 py-1.5 text-xs">
              <option value="">Any</option><option value="mobile">Mobile</option><option value="tablet">Tablet</option><option value="desktop">Desktop</option>
            </select>
          </div>
          <button onClick={applyFilters} className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs flex items-center gap-1" data-testid="intel-apply-filters">
            <Filter size={12} /> Apply
          </button>
        </div>
      </div>

      {busy && <p className="text-xs font-mono text-[var(--pg-ink-faint)]">Loading…</p>}
      {data?.error && <p className="text-xs text-[var(--pg-neon-pink)] font-mono">{data.error}</p>}

      {/* Behavioral */}
      {report === 'behavioral' && data && !data.error && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Card title="Events"   value={data.totals?.events || 0} />
            <Card title="Sessions" value={data.totals?.sessions || 0} />
            <Card title="Visitors" value={data.totals?.visitors || 0} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
              <p className="pg-label text-[10px] mb-3">ENTRY SOURCES</p>
              <div className="space-y-2">{(data.entry_sources || []).map((s) => (
                <BarRow key={s._id} label={s._id || 'direct'} value={s.count} max={data.entry_sources[0]?.count} />
              ))}</div>
            </div>
            <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
              <p className="pg-label text-[10px] mb-3">LANDING PAGES</p>
              <div className="space-y-2">{(data.landing_pages || []).map((s) => (
                <BarRow key={s._id} label={s._id || '/'} value={s.count} max={data.landing_pages[0]?.count} color="var(--pg-emerald-2)" />
              ))}</div>
            </div>
            <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
              <p className="pg-label text-[10px] mb-3">TOP TIME ON PAGE (avg ms)</p>
              <div className="space-y-2">{(data.top_time_on_page || []).map((s) => (
                <BarRow key={s._id} label={s._id || '/'} value={Math.round(s.avg_ms || 0)} max={data.top_time_on_page[0]?.avg_ms} />
              ))}</div>
            </div>
            <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
              <p className="pg-label text-[10px] mb-3">SCROLL DEPTH (avg %)</p>
              <div className="space-y-2">{(data.scroll_depth || []).map((s) => (
                <BarRow key={s._id} label={s._id || '/'} value={Math.round(s.avg_pct || 0)} max={100} color="var(--pg-neon-pink)" />
              ))}</div>
            </div>
            <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
              <p className="pg-label text-[10px] mb-3">DEVICES</p>
              <div className="space-y-2">{(data.devices || []).map((s) => (
                <BarRow key={s._id} label={s._id || 'unknown'} value={s.count} max={data.devices[0]?.count} />
              ))}</div>
            </div>
            <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
              <p className="pg-label text-[10px] mb-3">EXIT PAGES</p>
              <div className="space-y-2">{(data.exit_pages || []).map((s) => (
                <BarRow key={s._id} label={s._id || '/'} value={s.count} max={data.exit_pages[0]?.count || 1} color="var(--pg-neon-pink)" />
              ))}</div>
              {(data.exit_pages || []).length === 0 && <p className="text-[11px] font-mono text-[var(--pg-ink-faint)]">No exit intents recorded yet.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Content Performance */}
      {report === 'content' && data && !data.error && (
        <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden">
          {(data.rows || []).length === 0 ? (
            <p className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">No content-tagged events yet. Views tracked automatically on public pages.</p>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-[var(--pg-ink-faint)] border-b border-[var(--pg-border-2)]">
                <th className="px-4 py-2">Content</th><th>Views</th><th>Plays</th><th>Complete %</th><th>Avg time (ms)</th>
              </tr></thead>
              <tbody>{data.rows.map((r) => (
                <tr key={`${r._id.content_type}:${r._id.content_id}`} className="border-b border-[var(--pg-border-2)]">
                  <td className="px-4 py-2"><span className="text-[var(--pg-ink)]">{r.title || r._id.content_id}</span> <span className="text-[10px] text-[var(--pg-ink-faint)]">({r._id.content_type})</span></td>
                  <td>{r.views || 0}</td><td>{r.plays || 0}</td><td>{r.completion_rate_pct}%</td><td>{Math.round(r.avg_time_ms || 0)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* Discovery */}
      {report === 'discovery' && data && !data.error && (
        <div className="grid lg:grid-cols-2 gap-4">
          {[['Search Topics', data.top_topics, 'var(--pg-gold-2)'],
            ['Audiences', data.top_audiences, 'var(--pg-emerald-2)'],
            ['Primary Topics', data.top_primary, 'var(--pg-neon-pink)']].map(([title, rows, color]) => (
            <div key={title} className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
              <p className="pg-label text-[10px] mb-3">{title.toUpperCase()}</p>
              <div className="space-y-2">{(rows || []).map((s) => (
                <BarRow key={s.label} label={s.label} value={s.views} max={(rows || [])[0]?.views || 1} color={color} />
              ))}</div>
              {(rows || []).length === 0 && <p className="text-[11px] font-mono text-[var(--pg-ink-faint)]">No data yet.</p>}
            </div>
          ))}
          <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
            <p className="pg-label text-[10px] mb-3">MOST CONNECTED CONTENT</p>
            <ul className="space-y-1">{(data.most_connected || []).map((r) => (
              <li key={`${r.content_type}:${r.content_id}`} className="text-[11px] font-mono flex justify-between">
                <span className="text-[var(--pg-ink)] truncate">{r.title || r.content_id}</span>
                <span className="text-[var(--pg-ink-muted)]">{r.related_count} links</span>
              </li>
            ))}</ul>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {report === 'suggestions' && data && !data.error && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Card title="Displays" value={data.totals?.displays || 0} />
            <Card title="Clicks" value={data.totals?.clicks || 0} />
            <Card title="CTR" value={`${data.totals?.ctr_pct || 0}%`} />
          </div>
          <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-[var(--pg-ink-faint)] border-b border-[var(--pg-border-2)]">
                <th className="px-4 py-2">Rule</th><th>Displays</th><th>Clicks</th><th>CTR</th><th>Style</th><th>Destination</th>
              </tr></thead>
              <tbody>{(data.per_rule || []).map((r) => (
                <tr key={r.rule_id} className="border-b border-[var(--pg-border-2)]">
                  <td className="px-4 py-2 text-[var(--pg-ink)]">{r.name || r.rule_id}</td>
                  <td>{r.displays}</td><td>{r.clicks}</td><td>{r.ctr_pct}%</td><td>{r.display_style}</td><td>{r.destination_kind}</td>
                </tr>
              ))}</tbody>
            </table>
            {(data.per_rule || []).length === 0 && <p className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]">No suggestion telemetry yet.</p>}
          </div>
        </div>
      )}

      {/* Commerce */}
      {report === 'commerce' && data && !data.error && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card title="Orders" value={data.totals?.orders || 0} sub={`${data.totals?.paid_orders || 0} paid`} />
            <Card title="Revenue" value={`$${(data.totals?.revenue || 0).toFixed(2)}`} sub={`AOV $${data.totals?.aov || 0}`} />
            <Card title="Refunded" value={data.totals?.refunded_count || 0} />
            <Card title="Subscribers" value={data.totals?.subscribers_active || 0} sub="active" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Card title="Digital revenue"  value={`$${(data.totals?.digital_revenue || 0).toFixed(2)}`} />
            <Card title="Physical revenue" value={`$${(data.totals?.physical_revenue || 0).toFixed(2)}`} />
          </div>
          <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden">
            <p className="px-4 pt-4 pg-label text-[10px]">PRODUCT PERFORMANCE</p>
            <table className="w-full text-xs mt-2">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-[var(--pg-ink-faint)] border-b border-[var(--pg-border-2)]">
                <th className="px-4 py-2">Product</th><th>Units</th><th>Revenue</th>
              </tr></thead>
              <tbody>{(data.products || []).map((p) => (
                <tr key={p.product_id} className="border-b border-[var(--pg-border-2)]">
                  <td className="px-4 py-2 text-[var(--pg-ink)]">{p.title || p.product_id}</td>
                  <td>{p.units}</td><td>${p.revenue.toFixed(2)}</td>
                </tr>
              ))}</tbody>
            </table>
            {(data.products || []).length === 0 && <p className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]">No paid orders in range.</p>}
          </div>
        </div>
      )}

      {/* Traffic */}
      {report === 'traffic' && data && !data.error && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
            <p className="pg-label text-[10px] mb-3">TRAFFIC BREAKDOWN</p>
            <div className="space-y-2">{Object.entries(data.grouped || {}).map(([k, v]) => (
              <BarRow key={k} label={k} value={v} max={data.total || 1} color="var(--pg-gold-2)" />
            ))}</div>
          </div>
          <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4">
            <p className="pg-label text-[10px] mb-3">PERCENTAGES</p>
            <div className="space-y-2">{Object.entries(data.percentages || {}).map(([k, v]) => (
              <BarRow key={k} label={k} value={v} max={100} color="var(--pg-emerald-2)" />
            ))}</div>
          </div>
        </div>
      )}
    </div>
  );
}
